import {
  CircleHelp,
  Ellipsis,
  House,
  ShoppingCart,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useWorkspace } from '@/hooks/use-workspace';
import {
  findActiveModule,
  isUnderRoute,
  MOBILE_LABELS,
  MOBILE_PRIMARY_ROUTES,
} from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { WorkspaceSwitcher } from './workspace-switcher';

const PRIMARY_ICONS: Record<string, LucideIcon> = {
  '/dashboard': House,
  '/estoque': Warehouse,
  '/vendas': ShoppingCart,
};

const TYPING_SELECTOR =
  'input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=file]), textarea, select, [contenteditable=true]';

/**
 * Marca `<html data-typing>` enquanto um campo de texto tem foco, para a navegação inferior
 * sair do caminho do teclado virtual e os rodapés fixos descerem até a borda.
 */
function useTypingFlag() {
  useEffect(() => {
    const root = document.documentElement;
    const update = () => {
      const el = document.activeElement;
      if (el instanceof HTMLElement && el.matches(TYPING_SELECTOR)) root.dataset.typing = '';
      else delete root.dataset.typing;
    };
    // No focusout o novo foco ainda não foi aplicado: reavalia no próximo tick
    const deferred = () => window.setTimeout(update, 0);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', deferred);
    return () => {
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', deferred);
      delete root.dataset.typing;
    };
  }, []);
}

function TabLink({
  to,
  label,
  icon: Icon,
  active,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
}) {
  return (
    <NavLink
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg text-[11px] font-medium text-muted-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-primary/25',
        active && 'text-primary',
      )}
    >
      <span
        className={cn(
          'flex h-7 w-12 items-center justify-center rounded-full transition-colors',
          active && 'bg-primary-soft',
        )}
      >
        <Icon className="size-5" strokeWidth={1.75} aria-hidden />
      </span>
      {label}
    </NavLink>
  );
}

/** Navegação inferior (mobile): Início, Estoque, Vendas e Mais (demais módulos). */
export function MobileNav() {
  useTypingFlag();
  const { pathname } = useLocation();
  const { enabled: hasWorkspaces, workspace, modules } = useWorkspace();
  const [moreOpen, setMoreOpen] = useState(false);
  const primary = MOBILE_PRIMARY_ROUTES.filter((to) => modules.some((m) => m.to === to));
  const current = findActiveModule(pathname);
  const moreActive = !current || !primary.some((to) => to === current.to);

  return (
    <>
      <nav
        aria-label="Navegação principal"
        className="fixed inset-x-0 bottom-0 z-40 border-t bg-card/95 pb-safe backdrop-blur md:hidden in-data-typing:hidden"
      >
        <div className="flex h-(--bottom-nav-height) items-stretch gap-1 px-2 py-1.5">
          {primary.map((to) => (
            <TabLink
              key={to}
              to={to}
              label={MOBILE_LABELS[to] ?? to}
              icon={PRIMARY_ICONS[to] ?? House}
              active={isUnderRoute(pathname, to)}
            />
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            className={cn(
              'flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg text-[11px] font-medium text-muted-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-primary/25',
              moreActive && 'text-primary',
            )}
          >
            <span
              className={cn(
                'flex h-7 w-12 items-center justify-center rounded-full',
                moreActive && 'bg-primary-soft',
              )}
            >
              <Ellipsis className="size-5" strokeWidth={1.75} aria-hidden />
            </span>
            Mais
          </button>
        </div>
      </nav>
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="gap-0 md:hidden">
          <SheetHeader>
            <SheetTitle>Módulos</SheetTitle>
            <SheetDescription>
              {hasWorkspaces && workspace.modules
                ? `Área de trabalho: ${workspace.label}.`
                : 'Acesse qualquer área do sistema.'}
            </SheetDescription>
          </SheetHeader>
          <SheetBody className="py-3">
            {hasWorkspaces ? (
              <WorkspaceSwitcher
                className="mb-3 w-full max-w-none"
                onNavigateAway={() => setMoreOpen(false)}
              />
            ) : null}
            <ul className="grid grid-cols-3 gap-1 min-[420px]:grid-cols-4">
              {modules
                .filter((m) => !primary.some((to) => to === m.to))
                .map((m) => {
                  const active = current?.to === m.to;
                  return (
                    <li key={m.to}>
                      <NavLink
                        to={m.to}
                        onClick={() => setMoreOpen(false)}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex min-h-[4.5rem] flex-col items-center justify-center gap-1.5 rounded-xl px-1 py-2 text-center text-xs font-medium text-foreground outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-primary/25',
                          active && 'bg-primary-soft text-primary',
                        )}
                      >
                        <m.icon
                          className={cn(
                            'size-[22px]',
                            active ? 'text-primary' : 'text-muted-foreground',
                          )}
                          strokeWidth={1.75}
                          aria-hidden
                        />
                        <span className="line-clamp-2 leading-tight">
                          {m.shortLabel ?? m.label}
                        </span>
                      </NavLink>
                    </li>
                  );
                })}
              <li>
                <NavLink
                  to="/ajuda"
                  onClick={() => setMoreOpen(false)}
                  className="flex min-h-[4.5rem] flex-col items-center justify-center gap-1.5 rounded-xl px-1 py-2 text-center text-xs font-medium outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-primary/25"
                >
                  <CircleHelp
                    className="size-[22px] text-muted-foreground"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                  Ajuda
                </NavLink>
              </li>
            </ul>
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}
